import { Card, Divider, Grid, Typography } from '@mui/material';
import React from 'react';
import { Translate } from 'react-localize-redux';

export interface RequestManagementPanelProps {
    /** The per-item list with its row actions (Modifier / Supprimer). */
    services: React.ReactNode;
    /** The single add control. */
    addService: React.ReactNode;
    /** Status + the next action the server offers. */
    status: React.ReactNode;
    /** Cancellation — destructive, so it lives at the bottom behind a separator. */
    cancel: React.ReactNode;
}

/**
 * SHER-909 — one section for everything that can be done to a request.
 *
 * Before this, the four controls were siblings of equal weight: each brought its own bold
 * heading, the add control was a heading AND a button, and the surgical status panel sat in
 * a second Card of its own beside the first. Four headings for four controls is not
 * structure, it is noise, and it put "Annuler la demande" at the same visual level as
 * adding a line.
 *
 * The arrangement is the feature here, so it is a component rather than JSX inline in
 * RequestSingle: it can be seen in the catalogue in the states that matter, including the
 * ones where pieces are missing.
 *
 * Composition over props-drilling on purpose. Each control already decides for itself
 * whether the user may see it, and those rules differ (MODIFY_REQUESTS, CANCEL_REQUESTS,
 * UPDATE_LAB_REQUESTS, plus the server's own status list). Passing them in as nodes keeps
 * that knowledge where it already lives instead of giving this component a fifth copy of
 * the permission rules to drift out of step with.
 */
export const RequestManagementPanel: React.FC<RequestManagementPanelProps> = ({ services, addService, status, cancel }) => {
    const hasServices = Boolean(services) || Boolean(addService);
    // Render nothing at all rather than an empty titled Card. A read-only user gets no
    // panel, not a box announcing a section with nothing in it.
    if (!hasServices && !status && !cancel) {
        return null;
    }

    return (
        <Grid item xs={12}>
            <Card style={{ padding: '1rem' }} data-testid="request-management-panel">
                <Typography variant="h6" style={{ fontWeight: 'bold' }} gutterBottom>
                    <Translate id="pages.hospital.services.request.management.title" />
                </Typography>

                {hasServices &&
                    <Grid container spacing={1} direction="column">
                        {services && <Grid item>{services}</Grid>}
                        {addService && <Grid item>{addService}</Grid>}
                    </Grid>
                }

                {status &&
                    <div style={{ marginTop: hasServices ? '1rem' : 0 }}>{status}</div>
                }

                {/* Cancel is the one irreversible control in the panel, so it is demoted:
                    last, and separated by a rule so it cannot be clicked on the way to
                    something else. The separator only appears when something precedes it —
                    a rule at the top of an otherwise empty card is just a stray line. */}
                {cancel &&
                    <>
                        {(hasServices || status) && <Divider style={{ margin: '1rem 0 0.75rem' }} />}
                        <div data-testid="request-management-destructive">{cancel}</div>
                    </>
                }
            </Card>
        </Grid>
    );
};

export default RequestManagementPanel;
